<template>
  <div class="ei-scale-table">
    <template
        v-for="(section, sectionIndex) in normalizedSections"
        :key="section.key || sectionIndex"
    >
      <div
          v-if="shouldShowSectionTitle(section, sectionIndex)"
          class="ei-scale-table__section-title"
      >
        {{ section.title }}
      </div>

      <div class="ei-scale-table__rows">
        <div
            v-for="row in section.rows"
            :key="row.key || row.label"
            class="ei-scale-table__row"
        >
          <div class="ei-scale-table__row-header">
            <div class="ei-scale-table__label">
              <span>{{ row.label }}</span>

              <a-popover
                  v-if="row.helpText"
                  trigger="hover"
                  placement="top"
              >
                <template #content>
                  <div class="ei-scale-table__popover">
                    {{ row.helpText }}
                  </div>
                </template>

                <QuestionCircleOutlined class="ei-scale-table__help-icon" />
              </a-popover>
            </div>

            <span class="ei-scale-table__value">
              {{ formatValue(row.value) }}
            </span>
          </div>

          <div class="ei-scale-table__scale-area">
            <div class="ei-scale-table__track">
              <div
                  class="ei-scale-table__fill"
                  :style="{
                    width: getWidth(row.value) + '%',
                    backgroundColor: row.color,
                  }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <ScaleLabels
          v-if="showScaleAfterEachSection"
          :labels="scaleLabels"
      />
    </template>

    <ScaleLabels
        v-if="!showScaleAfterEachSection"
        :labels="scaleLabels"
        class="ei-scale-table__bottom-scale"
    />
  </div>
</template>

<script>
import { computed, defineComponent, h } from 'vue'
import { QuestionCircleOutlined } from '@ant-design/icons-vue'

const ScaleLabels = defineComponent({
  name: 'ScaleLabels',

  props: {
    labels: {
      type: Array,
      default: () => [],
    },
  },

  setup(props) {
    return () => h(
        'div',
        {
          class: 'ei-scale-table__scale',
        },
        props.labels.map((item) => h(
            'span',
            {
              key: item.key || item.value || item.label,
              class: 'ei-scale-table__scale-item',
            },
            [
              item.value !== '' && item.value !== null && item.value !== undefined
                  ? h('span', String(item.value))
                  : null,

              item.label
                  ? h('span', item.label)
                  : null,
            ]
        ))
    )
  },
})

export default {
  name: 'EiScaleRowsTable',

  components: {
    QuestionCircleOutlined,
    ScaleLabels,
  },

  props: {
    sections: {
      type: Array,
      default: () => [],
    },
    min: {
      type: Number,
      default: 50,
    },
    max: {
      type: Number,
      default: 150,
    },
    showScaleAfterEachSection: {
      type: Boolean,
      default: false,
    },
    showFirstSectionTitle: {
      type: Boolean,
      default: false,
    },
  },

  setup(props) {
    const scaleLabels = [
      {
        key: 'min',
        value: 50,
        label: '',
      },
      {
        key: 'low',
        value: '',
        label: 'Низкий',
      },
      {
        key: 'eighty',
        value: 80,
        label: '',
      },
      {
        key: 'average',
        value: '',
        label: 'Средний',
      },
      {
        key: 'hundred',
        value: 100,
        label: '',
      },
      {
        key: 'high',
        value: '',
        label: 'Высокий',
      },
      {
        key: 'one-twenty',
        value: 120,
        label: '',
      },
      {
        key: 'highest',
        value: '',
        label: 'Высший',
      },
      {
        key: 'max',
        value: 150,
        label: '',
      },
    ]

    const toNumber = (value) => {
      const number = Number(value)

      return Number.isFinite(number) ? number : 0
    }

    const normalizedSections = computed(() => {
      return props.sections.map((section) => ({
        ...section,
        rows: Array.isArray(section.rows)
            ? section.rows.map((row) => ({
              ...row,
              value: toNumber(row.value),
              color: row.color || '#9B8CFF',
            }))
            : [],
      }))
    })

    const getWidth = (value) => {
      const number = toNumber(value)

      if (number <= props.min) {
        return 0
      }

      if (number >= props.max) {
        return 100
      }

      return Number((((number - props.min) / (props.max - props.min)) * 100).toFixed(2))
    }

    const formatValue = (value) => {
      const number = toNumber(value)

      return Number.isInteger(number)
          ? number
          : number.toFixed(1)
    }

    const shouldShowSectionTitle = (section, sectionIndex) => {
      if (!section.title) {
        return false
      }

      if (sectionIndex === 0 && !props.showFirstSectionTitle) {
        return false
      }

      return true
    }

    return {
      scaleLabels,
      normalizedSections,
      getWidth,
      formatValue,
      shouldShowSectionTitle,
    }
  },
}
</script>

<style scoped lang="scss">
.ei-scale-table {
  width: 100%;
  display: flex;
  flex-direction: column;
  text-align: start;
}

.ei-scale-table__section-title {
  margin: 0 0 8px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.45);
}

.ei-scale-table__rows {
  display: flex;
  flex-direction: column;
}

.ei-scale-table__row {
  position: relative;
  margin-bottom: 8px;
}

.ei-scale-table__row::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: -8px;
  pointer-events: none;
  background-image:
      linear-gradient(
              90deg,
              transparent 0,
              transparent calc(30% - 1px),
              rgba(0, 0, 0, 0.045) calc(30% - 1px),
              rgba(0, 0, 0, 0.045) calc(30% + 1px),
              transparent calc(30% + 1px),

              transparent calc(50% - 1px),
              rgba(0, 0, 0, 0.045) calc(50% - 1px),
              rgba(0, 0, 0, 0.045) calc(50% + 1px),
              transparent calc(50% + 1px),

              transparent calc(70% - 1px),
              rgba(0, 0, 0, 0.045) calc(70% - 1px),
              rgba(0, 0, 0, 0.045) calc(70% + 1px),
              transparent calc(70% + 1px)
      );
  z-index: 0;
}

.ei-scale-table__row-header {
  position: relative;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 5px;
  font-size: 14px;
  line-height: 120%;
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-table__label {
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
}

.ei-scale-table__label span:first-child {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ei-scale-table__help-icon {
  flex: 0 0 auto;
  color: rgba(0, 0, 0, 0.35);
  font-size: 13px;
  cursor: pointer;
}

.ei-scale-table__popover {
  max-width: 240px;
  font-size: 13px;
  line-height: 130%;
}

.ei-scale-table__value {
  flex: 0 0 auto;
  min-width: 32px;
  text-align: right;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.88);
}

.ei-scale-table__scale-area {
  position: relative;
  z-index: 1;
  width: 100%;
  height: 4px;
}

.ei-scale-table__track {
  position: relative;
  width: 100%;
  height: 4px;
  overflow: hidden;
  background-color: rgba(238, 240, 243, 0.85);
  border-radius: 999px;
}

.ei-scale-table__fill {
  height: 100%;
  min-width: 0;
  border-radius: 999px;
}

.ei-scale-table__scale {
  display: grid;
  grid-template-columns: 34px 1fr 34px 1fr 34px 1fr 34px 1fr 34px;
  align-items: center;
  margin: 4px 0 16px;
  font-size: 12px;
  line-height: 110%;
  color: rgba(0, 0, 0, 0.28);
}

.ei-scale-table__scale-item {
  min-width: 0;
  text-align: center;
  white-space: nowrap;
}

.ei-scale-table__scale-item span + span {
  margin-left: 2px;
}

.ei-scale-table__bottom-scale {
  margin-top: 4px;
  margin-bottom: 0;
}

.ei-scale-table > :last-child {
  margin-bottom: 0;
}
</style>